<script setup lang="ts">
  import { computed, provide } from 'vue';
  import {
    isNodeShown,
    type CompiledForm,
    type CompiledNode,
    type FieldValue,
    type ResponseMarks,
    type ValidationIssue,
  } from '@record-fill';
  import { FILL_CONTEXT_KEY, type ChunkEdges } from './fill-context';
  import FillNode from './FillNode.vue';

  // An absent boolean prop is cast to false by Vue, so the "on by default" gate needs withDefaults.
  const props = withDefaults(
    defineProps<{
      form: CompiledForm;
      highlightedFieldId?: string | null;
      extraIssues?: ValidationIssue[];
      showAnchorHighlights?: boolean;
      showCompiledIssues?: boolean;
      readOnly?: boolean;
      changeMarks?: ResponseMarks | null;
      showHighlights?: boolean;
      chunkEdges?: ChunkEdges | null;
    }>(),
    {
      highlightedFieldId: null,
      extraIssues: () => [],
      showAnchorHighlights: false,
      showCompiledIssues: true,
      readOnly: false,
      changeMarks: null,
      showHighlights: true,
      chunkEdges: null,
    }
  );
  const emit = defineEmits<{
    (e: 'update:value', fieldId: string, value: FieldValue): void;
    (e: 'update:cell', tableId: string, rowIndex: number, columnIndex: number, value: FieldValue): void;
    (e: 'add-row', tableId: string): void;
    (e: 'remove-row', tableId: string, rowIndex: number): void;
    (e: 'focus:field', fieldId: string): void;
  }>();

  // Issues the caller computes outside the compiler (archive readiness, server errors) are
  // drawn on the field exactly like the compiled ones.
  const extraIssuesByField = computed<Record<string, ValidationIssue[]>>(() => {
    const byField: Record<string, ValidationIssue[]> = {};
    for (const issue of props.extraIssues) {
      byField[issue.fieldId] ??= [];
      byField[issue.fieldId].push(issue);
    }
    return byField;
  });

  provide(FILL_CONTEXT_KEY, {
    highlightedFieldId: computed(() => props.highlightedFieldId),
    extraIssuesByField,
    showAnchorHighlights: computed(() => props.showAnchorHighlights),
    // The compiler reports an empty required field at once; a screen that only wants errors after
    // the first save attempt switches this off and keeps the extra channel.
    showCompiledIssues: computed(() => props.showCompiledIssues),
    readOnly: computed(() => props.readOnly),
    changeMarks: computed(() => props.changeMarks),
    showHighlights: computed(() => props.showHighlights),
    chunkEdges: computed(() => props.chunkEdges),
    onFocusField: (fieldId: string) => emit('focus:field', fieldId),
  });

  // A node a rule hides must not keep its margin: an empty wrapper with a top
  // margin stacked the gaps of every hidden block above the next visible one.
  const visibleNodes = computed(() => props.form.nodes.filter(isNodeShown));

  // Top-level spacing comes from each element's own margin, as the canvas does; the header
  // keeps its per-page margins and is left alone here.
  function marginStyle(node: CompiledNode): Record<string, string> | undefined {
    if (node.kind === 'header' || node.kind === 'unsupported') return undefined;
    const margin = node.element.properties.margin;
    if (!margin) return undefined;
    return {
      marginTop: `${margin.top ?? 0}px`,
      marginRight: `${margin.right ?? 0}px`,
      marginBottom: `${margin.bottom ?? 0}px`,
      marginLeft: `${margin.left ?? 0}px`,
    };
  }
</script>

<template>
  <div
    class="light-surface flex flex-col bg-white-01 text-gray-07"
    data-testid="fill-renderer"
  >
    <div
      v-for="node in visibleNodes"
      :key="node.id"
      :style="marginStyle(node)"
      :data-sheet-node="node.kind"
      :data-sheet-id="node.id"
    >
      <FillNode
        :node="node"
        @update:value="(fieldId: string, value: FieldValue) => emit('update:value', fieldId, value)"
        @update:cell="(tableId: string, rowIndex: number, columnIndex: number, value: FieldValue) =>
          emit('update:cell', tableId, rowIndex, columnIndex, value)"
        @add-row="(tableId: string) => emit('add-row', tableId)"
        @remove-row="(tableId: string, rowIndex: number) => emit('remove-row', tableId, rowIndex)"
      />
    </div>
  </div>
</template>
